import { graphlib } from '@dagrejs/dagre';
import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import { groupBy } from 'es-toolkit/compat';
import { ERROR_GRAPH__base } from '../../_errors/ERROR_GRAPH__base';
import { ERROR_GRAPH__graph } from '../../_errors/ERROR_GRAPH__graph';
import { FN_FIELD_CONTAINER__get_fields } from '../../_fields/_base/_methods/FN_FIELD_CONTAINER__get_fields';
import { FIELD__base } from '../../_fields/_base/FIELD__base';
import { ENUM__field_role } from '../../_invocations/_base/ENUM__field_role';
import { ENUM__invocation_role } from '../../_invocations/_base/ENUM__invocation_role';
import { INVOCATION__base } from '../../_invocations/_base/INVOCATION__base';
import { DTO_GRAPH__edge } from '../DTO_GRAPH__edge';
import { DTO_GRAPH__instance } from '../DTO_GRAPH__instance';

declare module '../DTO_GRAPH__instance' {
  interface DTO_GRAPH__instance {
    revalidate(): void;

    validate(): void;

    validate_duplicates__nodes(): void;

    validate_duplicates__fields(): void;

    validate_edges(): void;

    /*
     * ERRORS PROPERTIES
     */

    /*
     * ERRORS MANAGEMENT
     */

    errors__clear(): void;

    errors__remove_duplicates(): void;

    error__add(error_type: ENUM__error_type): void;

    error__add__node(node: INVOCATION__base, error_type: ENUM__error_type): void;

    error__add__edge(edge: DTO_GRAPH__edge, error_type: ENUM__error_type): void;

    error__add__field(field: FIELD__base, error_type: ENUM__error_type): void;
  }
}

export function INIT_EXTENSION_DTO_GRAPH__instance_validate() {
  //do nothing
}

/**
 * Validate the graph. Checks that all edges have valid source and destination nodes.
 * @returns Array of error types, empty if validation passes.
 */

DTO_GRAPH__instance.prototype.revalidate = function (): void {
  this.errors__clear();

  this.validate();
};
DTO_GRAPH__instance.prototype.validate = function (): void {
  if (this.meta.is_validated) return;
  this.meta.is_validated = true;

  let graph_dagre = this.dagre__convert_to();

  let a = graphlib.alg.findCycles(graph_dagre);

  // todo: put a to error
  if (a.length > 0) debugger;

  if (!graphlib.alg.isAcyclic(graph_dagre)) return this.error__add(ENUM__error_type.GRAPH__IS_CYCLIC);

  this.validate_duplicates__nodes();
  this.validate_duplicates__fields();

  this.validate_edges();

  if (this.invocation_grid2d_view.is_stub()) this.error__add(ENUM__error_type.GRAPH__MISSING_GRID2D_IMAGE_OUT);

  for (const node of this.nodes_map.values()) {
    if (this.nodes_ids__exceptions_to_process.includes(node.meta.node_id)) continue;

    node.validate(this);

    this.meta.errors = [...this.meta.errors, ...node.meta.errors];

    if (node.is_stub()) this.error__add__node(node, ENUM__error_type.GRAPH_NODE__CANT_BE_STUB);
  }

  /*
   *
   */

  let [field_grid2d_row, field_grid2d_column] = [this.grid2d__get_field_row(), this.grid2d__get_field_column()];

  if (field_grid2d_row.meta.kind__is_output) this.error__add__field(field_grid2d_row, ENUM__error_type.GRAPH_FIELD__OUTPUT_CANT_BE_DIMENSION);
  if (field_grid2d_column.meta.kind__is_output) this.error__add__field(field_grid2d_column, ENUM__error_type.GRAPH_FIELD__OUTPUT_CANT_BE_DIMENSION);

  if (field_grid2d_row.meta.data__is_linked) this.error__add__field(field_grid2d_row, ENUM__error_type.GRAPH_FIELD__LINKED_FIELD_CANT_BE_DIMENSION);
  if (field_grid2d_column.meta.data__is_linked) this.error__add__field(field_grid2d_row, ENUM__error_type.GRAPH_FIELD__LINKED_FIELD_CANT_BE_DIMENSION);

  /*
   *
   */

  let [grid2d_row_count, grid2d_column_count] = this.grid2d__get_fields_dimensions();

  if (grid2d_row_count.length !== 1) this.error__add(ENUM__error_type.GRAPH__MISSING_GRID2D_ROW);
  if (grid2d_column_count.length !== 1) this.error__add(ENUM__error_type.GRAPH__MISSING_GRID2D_COLUMN);

  this.errors__remove_duplicates();
};

DTO_GRAPH__instance.prototype.validate_duplicates__nodes = function (): void {
  const nodes_by_id = new Map<string, INVOCATION__base[]>();
  const nodes_by_role = new Map<string, INVOCATION__base[]>();

  for (const node of this.nodes_map.values()) {
    nodes_by_id.add_item_to_array(node.meta.node_id, node);

    if (node.meta.node_role != ENUM__invocation_role.UNKNOWN) nodes_by_role.add_item_to_array(node.meta.node_role, node);
  }

  for (const nodes of nodes_by_id.values()) {
    if (nodes.length < 2) continue;

    for (const node of nodes) this.error__add__node(node, ENUM__error_type.GRAPH_NODE__DUPLICATE_ID);
  }

  for (const nodes of nodes_by_role.values()) {
    if (nodes.length < 2) continue;

    for (const node of nodes) this.error__add__node(node, ENUM__error_type.GRAPH_NODE__DUPLICATE_ROLE);
  }
};

DTO_GRAPH__instance.prototype.validate_duplicates__fields = function (): void {
  const fields_by_id = new Map<string, FIELD__base[]>();
  const fields_by_role = new Map<string, FIELD__base[]>();

  for (const node of this.nodes_map.values()) {
    let fields = FN_FIELD_CONTAINER__get_fields(node);

    for (const field of fields) {
      fields_by_id.add_item_to_array(field.meta.field_id, field);

      let { field_role_normalized } = field.meta;
      if (field_role_normalized != ENUM__field_role.UNKNOWN) fields_by_role.add_item_to_array(field_role_normalized, field);
    }
  }

  for (const fields of fields_by_id.values()) {
    if (fields.length < 2) continue;

    for (const field of fields) this.error__add__field(field, ENUM__error_type.GRAPH_FIELD__DUPLICATE_ID);
  }

  for (const fields of fields_by_role.values()) {
    if (fields.length < 2) continue;

    for (const field of fields) this.error__add__field(field, ENUM__error_type.GRAPH_FIELD__DUPLICATE_ROLE);
  }
};

DTO_GRAPH__instance.prototype.validate_edges = function () {
  for (const edge of this.edges) {
    if (!this.nodes_map.has(edge.field_from__node_id)) this.error__add__edge(edge, ENUM__error_type.GRAPH_EDGE__NODE_FROM_NOT_FOUND);
    if (!this.nodes_map.has(edge.field_to__node_id)) this.error__add__edge(edge, ENUM__error_type.GRAPH_EDGE__NODE_TO_NOT_FOUND);

    const is_duplicate = this.edges.filter(e => e !== edge).some(item => item.is_equals(edge));
    if (is_duplicate) this.error__add__edge(edge, ENUM__error_type.GRAPH_EDGE__DUPLICATE);
  }

  let key: keyof DTO_GRAPH__edge = 'field_to__field_id';

  for (const node of this.nodes_map.values()) {
    let fields_input = node.fields__get_connectable_inputs().values();

    const edges_to = this.edges__get_to(fields_input);

    // Validate that no node has multiple incoming edges with the same field
    let grouped = groupBy(edges_to, key);

    for (const [field_id, group] of Object.entries(grouped)) {
      // `Node ${node.id} has multiple incoming edges with field ${field}: ${group.map(String).join(', '}`
      let field = node.field__get(field_id);

      if (group.length > 1) {
        this.error__add__field(field, ENUM__error_type.GRAPH_FIELD__MULTIPLE_ENABLED_INCOMING_EDGES_ARE_NOT_ALLOWED);
      }
    }
  }
};

/*
 * ERRORS MANAGEMENT
 */
DTO_GRAPH__instance.prototype.errors__clear = function () {
  this.meta.errors_clear();

  for (const node of this.nodes_map.values()) {
    if (this.nodes_ids__exceptions_to_process.includes(node.meta.node_id)) continue;

    node.meta.errors_clear();

    let fields = node.get_fields_map(true).values();

    for (const field of fields) field.meta.errors_clear();
  }

  for (const edge of this.edges) edge.errors_clear();
};

DTO_GRAPH__instance.prototype.error__add = function (error_type: ENUM__error_type): void {
  let error = ERROR_GRAPH__graph.create(error_type, this.meta.graph_id);
  this.meta.errors.push(error);
};

DTO_GRAPH__instance.prototype.error__add__node = function (node: INVOCATION__base, error_type: ENUM__error_type): void {
  let error = node.error_add(error_type);
  this.meta.errors.push(error);
};

DTO_GRAPH__instance.prototype.error__add__edge = function (edge: DTO_GRAPH__edge, error_type: ENUM__error_type): void {
  let error = edge.error_add(error_type);
  this.meta.errors.push(error);
};

DTO_GRAPH__instance.prototype.error__add__field = function (field: FIELD__base, error_type: ENUM__error_type): void {
  let error = field.error_add(error_type);
  this.meta.errors.push(error);
};

DTO_GRAPH__instance.prototype.errors__remove_duplicates = function () {
  this.meta.errors = ERROR_GRAPH__base.get_unique(this.meta.errors);

  for (const node of this.nodes_map.values()) {
    node.meta.errors = ERROR_GRAPH__base.get_unique(node.meta.errors);

    let fields = node.get_fields_map(true).values();

    for (const field of fields) field.meta.errors = ERROR_GRAPH__base.get_unique(field.meta.errors);
  }

  for (const edge of this.edges) edge.errors = ERROR_GRAPH__base.get_unique(edge.errors);
};
